/**
 * General skills: plain stat boosts any fighter can bring in place of her own skills. They change her stats
 * before the fight, so her signature skills build on the boosted numbers. Kept weaker than signature skills.
 */
import type { Stats, Vfx } from './fighters.js';

/** A flat amount added to a stat; crit and lifesteal are shares, like 0.1 for 10%. */
export interface StatMod {
  stat: keyof BattleStats;
  add: number;
}

export interface GeneralSkill {
  /** Unique across general and signature skills; saved loadouts refer to skills by it. */
  id: string;
  name: string;
  on: 'general';
  /** The boost in short, like "+3 ATK". */
  effect: string;
  text: string;
  vfx: Vfx;
  mods: StatMod[];
}

/** `lifesteal` is the share of her attack damage she heals back. */
export type BattleStats = Stats & { lifesteal: number };

const skill = (s: Omit<GeneralSkill, 'on'>): GeneralSkill => ({ ...s, on: 'general' });

const all: GeneralSkill[] = [
  skill({
    id: 'sharpen',
    name: 'Sharpen',
    effect: '+3 ATK',
    text: 'Hits harder.',
    vfx: 'blood',
    mods: [{ stat: 'atk', add: 3 }],
  }),
  skill({
    id: 'keen-eye',
    name: 'Keen Eye',
    effect: '+10% crit',
    text: 'Crits more often.',
    vfx: 'lightning',
    mods: [{ stat: 'crit', add: 0.1 }],
  }),
  skill({
    id: 'quickstep',
    name: 'Quickstep',
    effect: '+3 SPD',
    text: 'Acts earlier in the round.',
    vfx: 'wind',
    mods: [{ stat: 'spd', add: 3 }],
  }),
  skill({
    id: 'vigor',
    name: 'Vigor',
    effect: '+20 HP',
    text: 'More max HP.',
    vfx: 'holy',
    mods: [{ stat: 'hp', add: 20 }],
  }),
  skill({
    id: 'iron-skin',
    name: 'Iron Skin',
    effect: '+3 DEF',
    text: 'Takes less damage.',
    vfx: 'tech',
    mods: [{ stat: 'def', add: 3 }],
  }),
  skill({
    id: 'bloodthirst',
    name: 'Bloodthirst',
    effect: '12% lifesteal',
    text: 'Heals herself for 12% of the attack damage she deals. Burns and other conditions don’t count.',
    vfx: 'blood',
    mods: [{ stat: 'lifesteal', add: 0.12 }],
  }),
];

const byId = new Map(all.map(s => [s.id, s]));

/** Her stats with the boosts of `skills` added on top. */
const apply = (stats: Stats, skills: GeneralSkill[]): BattleStats => {
  const mods = skills.flatMap(s => s.mods);
  const boosted = (stat: keyof BattleStats, base: number) =>
    Math.max(
      0,
      mods.reduce((sum, m) => (m.stat === stat ? sum + m.add : sum), base),
    );
  return {
    hp: boosted('hp', stats.hp),
    atk: boosted('atk', stats.atk),
    def: boosted('def', stats.def),
    spd: boosted('spd', stats.spd),
    crit: boosted('crit', stats.crit),
    lifesteal: boosted('lifesteal', 0),
  };
};

export const General = {
  all,
  find: (id: string): GeneralSkill | undefined => byId.get(id),
  apply,
};
